import { Action, createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  FieldIssue,
  Location,
  Technician,
  ST,
  pick,
  StCapacityCompany,
  ServiceTitanBusinessUnit,
  ServiceTitanJobType,
  ServiceTitanAdjustableCapacityDTO,
  ServiceTitanScheduledAppointment,
  TimezoneAbbreviationEnum,
  SetRequiredNonNullable,
  CallOutcomeEnum,
  isNewAppointmentOutcome
} from '_lib/api/apiTypes.shim';

import { RootState } from '_lib/redux/store';
import { DateUtils } from '_lib/utils/date.utils';
import { getTradeForNewCompany } from '_lib/utils/helpers';
import { ClearIssueItem, removeIssues } from '_lib/utils/validationErrorUtils';

import type { CustomerContext, SharedPhoneCallContext, SwitchProviderCompanyPayload } from '_lib/types/PhoneCall.types';

export interface PhoneCallContext
  extends Pick<
    SharedPhoneCallContext,
    'callOutcome' | 'serviceType' | 'trade' | 'notes' | 'internalNotes' | 'sendCompanyEmail' | 'sendEscalationEmail'
  > {
  cancelReason?: ST.JobCancelReason;
  businessUnit?: ServiceTitanBusinessUnit;
  jobType?: ServiceTitanJobType;
  location?: Location;
  appointmentTime?: string;
  previousServiceTitanAppointment?: ServiceTitanScheduledAppointment;
  overrideEmployeeId: boolean;
  assignedTech?: Technician;
  capacityItem?: ServiceTitanAdjustableCapacityDTO;
  companyTimezone: TimezoneAbbreviationEnum;
}

export const toSharedPhoneCallContext = (phoneCallContext: PhoneCallContext): SharedPhoneCallContext => ({
  callOutcome: phoneCallContext.callOutcome,
  internalNotes: phoneCallContext.internalNotes,
  notes: phoneCallContext.notes,
  sendCompanyEmail: phoneCallContext.sendCompanyEmail,
  sendEscalationEmail: phoneCallContext.sendEscalationEmail,
  trade: phoneCallContext.trade,
  serviceType: phoneCallContext.serviceType,
  previousAppointmentDate:
    (phoneCallContext.callOutcome === CallOutcomeEnum.Reschedule &&
      phoneCallContext.previousServiceTitanAppointment &&
      DateUtils.toDateString(phoneCallContext.previousServiceTitanAppointment.start, {
        timezone: phoneCallContext.companyTimezone
      })) ||
    null,
  appointmentDateTime:
    (isNewAppointmentOutcome(phoneCallContext.callOutcome) && phoneCallContext.appointmentTime) || null
});

/** Subset of data that we can safely carry over when switching tenants, or upon a re-initialization from error */
const safeDataKeys = [
  'appointmentTime',
  'callOutcome',
  'internalNotes',
  'notes',
  'overrideEmployeeId',
  'sendCompanyEmail',
  'sendEscalationEmail',
  'serviceType'
] as const satisfies (keyof PhoneCallContext)[];

type InitialPhoneCallContext = SetRequiredNonNullable<Partial<PhoneCallContext>, 'companyTimezone'>;

const initialPhoneCallContext = (ctx: InitialPhoneCallContext): PhoneCallContext => ({
  appointmentTime: ctx.appointmentTime,
  callOutcome: ctx.callOutcome,
  cancelReason: ctx.cancelReason,
  businessUnit: ctx.businessUnit,
  jobType: ctx.jobType,
  location: ctx.location,
  internalNotes: ctx.internalNotes || '',
  notes: ctx.notes || '',
  overrideEmployeeId: !!ctx.overrideEmployeeId,
  assignedTech: ctx.assignedTech,
  previousServiceTitanAppointment: ctx.previousServiceTitanAppointment,
  sendCompanyEmail: !!ctx.sendCompanyEmail,
  sendEscalationEmail: !!ctx.sendEscalationEmail,
  serviceType: ctx.serviceType || null,
  trade: ctx.trade || null,
  capacityItem: ctx.capacityItem,
  companyTimezone: ctx.companyTimezone
});

interface StCapacityPhoneCallSlice {
  customerContext: CustomerContext | null;
  phoneCallContext: PhoneCallContext;
  phoneCallFieldIssues: FieldIssue[];
  phoneCallNonFieldErrors: string[];
}

const initialState = (phoneCallContext: InitialPhoneCallContext): StCapacityPhoneCallSlice => ({
  customerContext: null,
  phoneCallContext: initialPhoneCallContext(phoneCallContext),
  phoneCallFieldIssues: [],
  phoneCallNonFieldErrors: []
});

type SetCustomerContextAction = PayloadAction<CustomerContext | null>;

export const stCapacityPhoneCallSlice = createSlice({
  name: 'stCapacityPhoneCallSlice',
  initialState: initialState({ companyTimezone: TimezoneAbbreviationEnum.EST }),
  reducers: {
    setCustomerContext(state, action: SetCustomerContextAction) {
      state.customerContext = action.payload;
    },
    setCustomerContextLocations(state, action: PayloadAction<CustomerContext['locations']>) {
      if (state.customerContext) state.customerContext.locations = action.payload;
    },
    setPartialPhoneCallContext(
      state,
      action: PayloadAction<{
        updates: Partial<PhoneCallContext>;
        clearIssues?: ClearIssueItem[];
      }>
    ) {
      state.phoneCallContext = { ...state.phoneCallContext, ...action.payload.updates };
      if (state.phoneCallFieldIssues.length && action.payload.clearIssues?.length) {
        const updatedIssues = action.payload.clearIssues.reduce(
          (acc, item) => removeIssues({ ...(typeof item === 'string' ? { issuePath: item } : item), issues: acc }),
          state.phoneCallFieldIssues
        );
        if (updatedIssues.length !== state.phoneCallFieldIssues.length) state.phoneCallFieldIssues = updatedIssues;
      }
    },
    setPhoneCallFieldIssues(state, action: PayloadAction<FieldIssue[]>) {
      state.phoneCallFieldIssues = action.payload;
    },
    setPhoneCallNonFieldErrors(state, action: PayloadAction<string[]>) {
      state.phoneCallNonFieldErrors = action.payload;
    },
    initialize: (
      state,
      action: PayloadAction<{ company: StCapacityCompany; options?: { keepExistingCallData: boolean } }>
    ) => {
      const { company, options } = action.payload;

      const trade = getTradeForNewCompany(
        (options?.keepExistingCallData && state.phoneCallContext.trade) || null,
        company
      );

      return initialState({
        ...(options?.keepExistingCallData ? pick(state.phoneCallContext, safeDataKeys) : {}),
        companyTimezone: company.timezone,
        trade
      });
    },
    switchDifferentTenantCompany: (state, action: PayloadAction<StCapacityCompany>) => {
      return {
        ...initialState({
          ...pick(state.phoneCallContext, safeDataKeys),
          companyTimezone: action.payload.timezone,
          trade: getTradeForNewCompany(state.phoneCallContext.trade, action.payload)
        }),
        phoneCallFieldIssues: state.phoneCallFieldIssues,
        phoneCallNonFieldErrors: state.phoneCallNonFieldErrors
      };
    },
    switchDifferentProviderCompany: (_state, action: PayloadAction<SwitchProviderCompanyPayload>) => {
      const { company, ...phoneCallContext } = action.payload || {};
      return initialState({
        ...phoneCallContext,
        companyTimezone: company.timezone,
        trade: getTradeForNewCompany(phoneCallContext.trade, company)
      });
    },
    /** Used to ensure we have a valid trade selected when switching companies */
    normalizeTradeFromCompany: (state, action: PayloadAction<StCapacityCompany>) => {
      state.phoneCallContext = {
        ...state.phoneCallContext,
        trade: getTradeForNewCompany(state.phoneCallContext.trade, action.payload)
      };
    },
    reset: () => initialState({ companyTimezone: TimezoneAbbreviationEnum.EST })
  },
  selectors: {
    selectCustomerContext: (state) => state.customerContext,
    selectPhoneCallContext: (state) => state.phoneCallContext,
    selectPhoneCallFieldIssues: (state) => state.phoneCallFieldIssues,
    selectPhoneCallNonFieldErrors: (state) => state.phoneCallNonFieldErrors
  }
});

export const {
  setCustomerContext,
  setCustomerContextLocations,
  setPartialPhoneCallContext,
  setPhoneCallFieldIssues,
  setPhoneCallNonFieldErrors,
  initialize,
  switchDifferentTenantCompany,
  switchDifferentProviderCompany,
  normalizeTradeFromCompany,
  reset
} = stCapacityPhoneCallSlice.actions;

export const {
  selectCustomerContext,
  selectPhoneCallContext,
  selectPhoneCallFieldIssues,
  selectPhoneCallNonFieldErrors
} = stCapacityPhoneCallSlice.getSelectors((rootState: RootState) => rootState.stCapacityPhoneCallSlice);

/**
 * @returns whether or not the action is a SetCustomerContextAction AND the customer has changed.
 */
export const isCustomerChangePredicate = (
  action: Action,
  currentState: RootState,
  originalState: RootState
): action is SetCustomerContextAction =>
  setCustomerContext.match(action) &&
  currentState.stCapacityPhoneCallSlice.customerContext?.customer.providerId !==
    originalState.stCapacityPhoneCallSlice.customerContext?.customer.providerId;
